import {
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  Flex,
  Timeline,
  Toggle,
} from "@components/index";
import { useGanttDataContext } from "@pages/Dashboard/contexts/GanttData.context";
import { useFilterContext } from "@pages/Dashboard/contexts/Filter.context";
import { useInitialScrollToNow } from "../hooks/useInitialScrollToNow";
import { useAutoScrollToNow } from "../hooks/useAutoScrollToNow";
import { useDragScroll } from "../hooks/useDragScroll";
import styles from "../Gantt.module.scss";

interface GanttDetalhadaProps {
  day: string;
  isFetched: boolean;
  isFetching: boolean;
}

// 19 horas total (7h às 2h do dia seguinte) = 19 * 60 minutos = 1140 minutos
// 1140 minutos * ~5.6px por minuto ≈ 6400px (mantém proporção similar)
const FIXED_WIDTH = 6400;
const START_HOUR = 7;
const END_HOUR = 2; // 2h do dia seguinte

/**
 * Componente Gantt para visão detalhada
 * Exibe uma única lane com todos os eventos de D1 e D2 (7h-2h do dia seguinte)
 * Com scroll horizontal e centralização automática do "now"
 */
const GanttDetalhada: React.FC<GanttDetalhadaProps> = ({ day, isFetched, isFetching }) => {
  const { date } = useFilterContext();
  const { mapGroupD1 = [] } = useGanttDataContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);
  const [dataKey, setDataKey] = useState(0);

  // Calcula o dia seguinte para o end (2h do dia seguinte)
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  const nextDayStr = nextDay.toISOString().split("T")[0];

  const start = new Date(`${day}T07:00:00`);
  const end = new Date(`${nextDayStr}T02:00:00`);

  const handleDragStart = useCallback(() => {
    // Qualquer drag manual deve desativar o auto-scroll,
    // mas sem exigir soltar o mouse e clicar de novo.
    setAutoScrollEnabled((prev) => (prev ? false : prev));
  }, []);

  const handleToggleChange = (value: boolean) => {
    // Liga/desliga o scroll automático explicitamente pelo usuário
    setAutoScrollEnabled(value);
  };

  // Atualiza dataKey sempre que a API termina de retornar (isFetching muda de true para false)
  useEffect(() => {
    if (!isFetching && isFetched) {
      setDataKey(prev => prev + 1);
    }
  }, [isFetching, isFetched]);

  // Scroll inicial: apenas uma vez quando o componente é montado
  useInitialScrollToNow({
    containerRef: scrollContainerRef,
    enabled: !autoScrollEnabled, // Só executa se auto scroll estiver desabilitado
    isFetched,
    date,
    startHour: START_HOUR,
    endHour: END_HOUR,
    fixedWidth: FIXED_WIDTH,
  });

  // Scroll automático: mantém o "now" sempre no meio quando toggle está ativo
  useAutoScrollToNow({
    containerRef: scrollContainerRef,
    enabled: autoScrollEnabled,
    isFetched,
    date,
    startHour: START_HOUR,
    endHour: END_HOUR,
    fixedWidth: FIXED_WIDTH,
    dataKey,
  });

  // Scroll manual via click + arrastar
  useDragScroll({
    containerRef: scrollContainerRef,
    onDragStart: handleDragStart,
  });

  return (
    <Flex.Col className={styles["content-detalhada-wrapper"]}>
      <Flex.Row className={styles["toggle-container"]}>
        <label htmlFor="auto-scroll-toggle">Scroll automático</label>
        <Toggle
          id="auto-scroll-toggle"
          checked={autoScrollEnabled}
          onChange={handleToggleChange}
        />
      </Flex.Row>
      <div 
        ref={scrollContainerRef}
        className={`${styles.content} ${styles["content-detalhada"]} ${autoScrollEnabled ? styles["smooth-scroll"] : ""}`}
      >
        <Timeline
          height="100%"
          start={start}
          end={end}
          groups={mapGroupD1}
          className={styles["timeline-detalhada"]}
          fixedWidth={FIXED_WIDTH}
        />
      </div>
    </Flex.Col>
  );
};

export default GanttDetalhada;
